import {ReactElement} from "react"
import {Icon} from "@iconify/react"
import {useTranslate} from "@refinedev/core"
import {Button, Col, Form, Image, Input, Row, Tag, Typography} from "antd"
import {PlusOutlined} from "@ant-design/icons"
import IClient from "../interfaces/IClient"
import IAuthentication from "../interfaces/IAuthentication"
import IType from "../interfaces/IType"

const {Title, Text} = Typography;

interface IImageProps {
    background?: string
    logo: string | null
    size?: number
}

interface IInfoProps {
    bold?: boolean
    color?: string
    icon: ReactElement | null
    label: string
    span?: number
    title?: string | ReactElement
    value?: string
}

interface IInformationProps {
    background?: string
    bold?: boolean
    color?: string
    icon: string | null
    label: string
    size?: number
    title?: string | ReactElement
    value?: string
}

interface ITitleProps {
    children?: any,
    icon?: string,
    iconSize?: number,
    title?: string,
    style?: any,
    light?: boolean,
}

type IInfo2Props = {
    horizontal?: boolean,
    value?: any,
    fallback?: string,
    labelSpan?: number,
    labelValue?: number,
} & ITitleProps

const Info = (props: IInfoProps): ReactElement | null => {
    const translate = useTranslate()
    return <>
        <Row style={{marginBottom: props.bold ? 5 : 0}}>
            <Col span={props.span}>
                <Row align="middle">
                    {props.icon}
                    <Text style={{
                        marginLeft: props.icon ? 5 : 0,
                        marginRight: props.title ? 10 : 0,
                        fontWeight: props.bold ? '600' : 'normal',
                        color: props.color ?? ''
                    }}>{translate(props.label)}</Text>
                    {props.title}
                </Row>
            </Col>
            <Col span={props.span && (24 - props.span)}>
                {props.value && !props.bold && (<Text>&nbsp;{props.value ?? ''}</Text>)}
            </Col>
        </Row>
        {props.value && props.bold && (<Row>&nbsp;{props.value ?? ''}</Row>)}
    </>
}

export const Custom = {
    formate: (duration: number, error: string): string => {
        return duration ? `${~~(duration / 60)}h${String(duration % 60).padStart(2, '0')}` : error;
    },
    AuthInfo: (props: { auth: IAuthentication, size?: number, type: IType }): ReactElement | null => {
        return (<Custom.ImageInfo icon={props.auth?.logo ?? null} size={props.size}
                                  label={`${props.type?.name} - ${props.auth?.name}`} background={props.auth?.color}/>
        )
    },
    ClientInfo: (props: { client: IClient, size?: number }): ReactElement | null => {
        return (
            <Custom.ImageInfo
                icon={props.client?.logo} size={props.size} label={props.client?.name} color={props.client?.color}/>
        )
    },
    IconInfo: (props: IInformationProps): ReactElement | null => {
        return (
            <Info icon={<Icon fontSize={props.size ?? 15} icon={`ant-design:${props.icon}-outlined`}/>}
                  label={props.label} value={props.value} span={props.value ? 12 : 24}/>
        )
    },
    IconTitle: (props: IInformationProps): ReactElement | null => {
        return (
            <Info bold icon={<Icon fontSize={props.size ?? 24} icon={`ant-design:${props.icon}-outlined`}/>}
                  label={props.label} value={props.value} title={props.title}/>
        )
    },
    Image: (props: IImageProps): ReactElement | null => {
        return <Image src={`${window.userprofile.url}/${window.userprofile.path.icon}/${props.logo}.png`}
                      width={props.size ?? 15} preview={false} style={{backgroundColor: props.background ?? ''}}/>
    },
    ImageInfo: (props: IInformationProps): ReactElement | null => {
        return (
            <Info icon={props.icon ?
                <Custom.Image background={props.background} size={props.size} logo={props.icon}/> : null}
                  label={props.label} color={props.color} value={props.value}/>
        )
    },
    HiddenItem: (props: any): ReactElement | null => {
        return (
            <Form.Item hidden name={props.name} initialValue={props.initialValue}><Input type="hidden"/></Form.Item>
        )
    },
    ItemInfo: (props: any): ReactElement | null => {
        return (
            <Form.Item name={props.name} label={<Custom.IconInfo label={props.label} icon={props.icon}/>}
                       rules={[{required: props.required}]} labelCol={{span: 4}} wrapperCol={{span: 20}}
                       valuePropName={props.valuePropName ?? 'value'}>
                {props.children}
            </Form.Item>
        )
    },
    Item: (props: any): ReactElement | null => {
        return (
            <Form.Item name={props.name} label={<Custom.IconTitle label={props.label} icon={props.icon}/>}
                       rules={[{required: props.required}]} labelCol={{span: props.horizontal ? 4 : 24}}
                       wrapperCol={{span: props.horizontal ? 20 : 24}}>
                {props.children}
            </Form.Item>
        )
    },
    List: (props: any): ReactElement | null => {
        const translate = useTranslate()
        return (
            <Form.List name={props.name}>
                {(formList, {add, remove}) => <>
                    <Custom.IconTitle label={props.label} icon={props.icon}
                                      title={props.editable &&
                                          <Button onClick={() => add()}><PlusOutlined/>{translate('buttons.add')}
                                          </Button>}/>
                    <Row gutter={[16, 16]}>
                        {typeof props.children === 'function' ? props.children(formList, remove) : props.children}
                    </Row>
                </>}
            </Form.List>
        )
    },
    Tag: (props: any): ReactElement | null => {
        return (
            <Tag color={props.auth?.color} closable={props.closable} onClose={props.onClose}>
                {props?.auth?.logo && <Custom.Image logo={props.auth?.logo} size={18}/>}
                {`${props.type?.name} - ${props.auth?.name}`}
            </Tag>
        )
    },
    Title: (props: ITitleProps): ReactElement | null => {
        const translate = useTranslate()
        return (
            <Row align="middle" style={{...props.style, marginBottom: 8}}>
                {props.icon &&
                    <Icon style={{marginRight: 4}} icon={props.icon} fontSize={props.iconSize ?? 22}/>
                }
                <Text style={{marginRight: 8}}>
                    {props?.light ?
                        translate(props?.title ?? "") ?? "Sans titre"
                        :
                        <strong>{translate(props?.title ?? "") ?? "Sans titre"}</strong>
                    }
                </Text>
                {props.children}
            </Row>
        )
    },
    Info: (props: IInfo2Props): ReactElement | null => {
        const translate = useTranslate()
        return (
            <>
                {props?.horizontal ?
                    <Row>
                        <Col span={props?.labelSpan}>
                            <Custom.Title title={props?.title} icon={props?.icon} iconSize={props?.iconSize}
                                          light={props?.light} style={props?.style}>
                                {props.children}
                            </Custom.Title>
                        </Col>
                        <Col span={props?.labelValue}>
                            <Text>{props?.value ?? (translate(props?.fallback ?? "") ?? undefined)}</Text>
                        </Col>
                    </Row>
                    :
                    <>
                        <span>
                            <Custom.Title title={props?.title} icon={props?.icon} iconSize={props?.iconSize}
                                          light={props?.light} style={props?.style}>
                                {props.children}
                            </Custom.Title>
                            <Text>{props?.value ?? (translate(props?.fallback ?? "") ?? undefined)}</Text>
                        </span>
                    </>
                }
            </>
        )
    }
}


/**
 * Adds a unique "key" to all the elements of an object, useful for displaying refine,
 * which requires a unique key for all its elements.
 * @param object - The object you want to "keyed"
 */
export const keyedObject = (object: any): any => {
    return object.map((child: any, index: number) => ({...child, key: index}))
}